import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search, Star, X, ChevronDown, ChevronRight, Clock,
  BarChart3, TrendingUp, Zap, Activity, Globe,
  Database, FileText, Calculator, DollarSign, PieChart,
  LineChart, Layers, Package, Settings,
  Users, Bell, ShieldCheck, MapPin, Cpu,
  Briefcase, Target, BookOpen, LayoutGrid, Wrench,
  Filter, Download, Upload, RefreshCw, Gauge,
  Building2, FlameKindling, Bolt, Leaf, Wind,
} from 'lucide-react';
import { apiFetch, redirectToApp } from '../../utils/auth';

const DRAWER_W = 640;
const RECENT_KEY = 'galaxy_recent_apps';
const MAX_RECENT = 6;

const ACCENT_COLORS = [
  '#3b82f6', '#10b981', '#8b5cf6', '#06b6d4',
  '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

const ICONS = [
  BarChart3, TrendingUp, Zap, Activity, Globe,
  Database, FileText, Calculator, DollarSign, PieChart,
  LineChart, Layers, Package, Clock, Settings,
  Users, Bell, ShieldCheck, MapPin, Cpu,
  Briefcase, Target, BookOpen, LayoutGrid, Wrench,
  Filter, Download, Upload, Gauge, RefreshCw,
  Building2, FlameKindling, Bolt, Leaf, Wind,
];

const tokens = (isDark) => isDark ? {
  page: '#020817',
  surface: 'rgba(255,255,255,0.04)',
  surfaceHover: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',
  text: '#f1f5f9',
  textSub: 'rgba(255,255,255,0.55)',
  accent: '#38bdf8',
  accentDim: 'rgba(56,189,248,0.12)',
  searchBg: 'rgba(255,255,255,0.05)',
  divLine: 'linear-gradient(90deg, rgba(56,189,248,0.45) 0%, transparent 100%)',
  star: '#FFD024',
} : {
  page: 'rgba(248,252,255,0.98)',
  surface: 'rgba(255,255,255,0.82)',
  surfaceHover: '#ffffff',
  border: 'rgba(14,116,144,0.12)',
  borderStrong: 'rgba(14,116,144,0.28)',
  text: '#0f172a',
  textSub: '#64748b',
  accent: '#0e7490',
  accentDim: 'rgba(14,116,144,0.08)',
  searchBg: 'rgba(14,116,144,0.06)',
  divLine: 'linear-gradient(90deg, rgba(14,116,144,0.4) 0%, transparent 100%)',
  star: '#a58103',
};

const timeAgo = (ts) => {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const MiniDashboardDrawer = ({
  open,
  onClose,
  apps,
  favorites: favoritesProp,
  expandedApps,
  setExpandedApps,
  groupAppsByAppname,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tk = useMemo(() => tokens(isDark), [isDark]);

  const [searchQuery, setSearchQuery] = useState('');
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const [favorites, setFavorites] = useState(favoritesProp || []);
  const [favSectionOpen, setFavSectionOpen] = useState(true);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [recentApps, setRecentApps] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
    catch { return []; }
  });

  const searchRef = useRef(null);
  const spotlightItemRefs = useRef([]);

  useEffect(() => { setFavorites(favoritesProp || []); }, [favoritesProp]);

  // Reset search + spotlight whenever the drawer closes.
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSpotlightOpen(false);
    }
  }, [open]);

  const groupAppsByCategory = useMemo(() => {
    const grouped = {};
    apps?.forEach((app) => {
      if (!grouped[app.category]) grouped[app.category] = {};
      if (!grouped[app.category][app.subcategory]) grouped[app.category][app.subcategory] = [];
      grouped[app.category][app.subcategory].push(app);
    });
    return grouped;
  }, [apps]);

  // Stable per-appname icon and accent so the drawer matches the main launcher.
  const appIconMap = useMemo(() => {
    const m = new Map();
    let i = 0;
    apps?.forEach((a) => { if (!m.has(a.appname)) { m.set(a.appname, ICONS[i % ICONS.length]); i++; } });
    return m;
  }, [apps]);

  const appColorMap = useMemo(() => {
    const m = new Map();
    let i = 0;
    apps?.forEach((a) => { if (!m.has(a.appname)) { m.set(a.appname, ACCENT_COLORS[i % ACCENT_COLORS.length]); i++; } });
    return m;
  }, [apps]);

  const getIcon = (name, size = 16) => {
    const C = appIconMap.get(name);
    return C ? <C size={size} /> : null;
  };

  const saveRecent = (app) => {
    const entry = {
      id: app.id, appname: app.appname, name: app.name,
      url: app.url, subcategory: app.subcategory, timestamp: Date.now(),
    };
    const updated = [entry, ...recentApps.filter((r) => r.id !== app.id)].slice(0, MAX_RECENT);
    setRecentApps(updated);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(updated)); } catch {}
  };

  const handleAppClick = (app) => {
    saveRecent(app);
    redirectToApp(app.url, app.requiredPermission, () => {});
  };

  const handleFavoriteToggle = async (id) => {
    const updated = favorites.includes(id) ? favorites.filter((x) => x !== id) : [...favorites, id];
    setFavorites(updated);
    try {
      const res = await apiFetch('login/favorites/', {
        method: 'POST',
        body: JSON.stringify({ favorites: updated }),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = res ? await res.json() : null;
      if (!data?.success) setFavorites(favorites);
    } catch (e) {
      console.error(e);
      setFavorites(favorites);
    }
  };

  const toggleCategoryCollapse = (sub) =>
    setCollapsedCategories((p) => ({ ...p, [sub]: !p[sub] }));

  // ─── Search ───────────────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return (apps || []).filter(
      (a) => a.appname?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q)
    );
  }, [searchQuery, apps]);

  useEffect(() => { setSpotlightIndex(0); }, [searchQuery]);
  useEffect(() => {
    if (!spotlightOpen) return;
    const el = spotlightItemRefs.current[spotlightIndex];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [spotlightIndex, spotlightOpen, searchResults.length]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!searchResults.length) return;
      setSpotlightOpen(true);
      setSpotlightIndex((i) => (i + 1) % searchResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!searchResults.length) return;
      setSpotlightOpen(true);
      setSpotlightIndex((i) => (i - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === 'Enter') {
      const target = searchResults[spotlightIndex];
      if (target) {
        e.preventDefault();
        handleAppClick(target);
        setSearchQuery('');
        setSpotlightOpen(false);
        searchRef.current?.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (searchQuery) {
        setSearchQuery('');
        setSpotlightOpen(false);
      } else {
        onClose();
      }
    }
  };

  // ─── Sub-components ───────────────────────────────────────────────────────

  const AppRow = ({ appname, appGroup, dense = false }) => {
    const color = appColorMap.get(appname) || tk.accent;
    const hasSubApps = appGroup.length > 1 && appGroup.some((a) => a.name);
    const single = appGroup[0];
    const expanded = !!expandedApps?.[appname];
    const isFavSingle = !hasSubApps && favorites.includes(single.id);

    return (
      <Box>
        <Box
          onClick={() =>
            hasSubApps
              ? setExpandedApps((p) => ({ ...p, [appname]: !p[appname] }))
              : handleAppClick(single)
          }
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25,
            px: 1.25, py: dense ? 0.75 : 1,
            borderRadius: '8px', cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': { bgcolor: tk.surfaceHover },
          }}
        >
          <Box sx={{
            width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
            bgcolor: `${color}1c`, border: `1px solid ${color}33`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color,
          }}>
            {getIcon(appname, 14) || (
              <Typography sx={{ fontSize: '12px', fontWeight: 800, color }}>
                {(appname?.[0] || '?').toUpperCase()}
              </Typography>
            )}
          </Box>

          <Typography sx={{
            flex: 1, fontSize: '13px', fontWeight: 500, color: tk.text,
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {appname}
          </Typography>

          {hasSubApps && (
            <Typography sx={{ fontSize: '11px', color: tk.textSub, flexShrink: 0 }}>
              {appGroup.length}
            </Typography>
          )}

          {!hasSubApps && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleFavoriteToggle(single.id); }}
              sx={{
                p: 0.3, flexShrink: 0,
                color: isFavSingle ? tk.star : tk.textSub,
                opacity: isFavSingle ? 1 : 0.35,
                transition: 'all 0.15s',
                '&:hover': { color: tk.star, opacity: 1 },
              }}
            >
              <Star size={12} fill={isFavSingle ? 'currentColor' : 'none'} />
            </IconButton>
          )}

          <Box sx={{
            flexShrink: 0,
            transition: 'transform 0.2s',
            transform: hasSubApps && expanded ? 'rotate(90deg)' : 'none',
            opacity: hasSubApps ? 1 : 0.3,
            color: tk.textSub,
            display: 'flex', alignItems: 'center',
          }}>
            <ChevronRight size={14} />
          </Box>
        </Box>

        {hasSubApps && expanded && (
          <Box sx={{ ml: 4.25, borderLeft: `1px solid ${tk.border}`, pl: 1.25, my: 0.25 }}>
            {appGroup.map((app, idx) => app.name && (
              <Box
                key={app.id}
                onClick={() => handleAppClick(app)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1, py: 0.5, borderRadius: '6px', cursor: 'pointer',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: tk.surfaceHover },
                }}
              >
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  bgcolor: ACCENT_COLORS[idx % ACCENT_COLORS.length],
                  boxShadow: `0 0 4px ${ACCENT_COLORS[idx % ACCENT_COLORS.length]}80`,
                }} />
                <Typography sx={{
                  flex: 1, fontSize: '12px', fontWeight: 500, color: tk.textSub,
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {app.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleFavoriteToggle(app.id); }}
                  sx={{
                    p: 0.2, flexShrink: 0,
                    color: favorites.includes(app.id) ? tk.star : tk.textSub,
                    opacity: favorites.includes(app.id) ? 1 : 0.35,
                    transition: 'all 0.15s',
                    '&:hover': { color: tk.star, opacity: 1 },
                  }}
                >
                  <Star size={11} fill={favorites.includes(app.id) ? 'currentColor' : 'none'} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // ─── Spotlight dropdown ───────────────────────────────────────────────────
  const spotlightDropdown = spotlightOpen && (searchQuery || searchResults.length > 0) && (
    <Box
      onMouseDown={(e) => e.preventDefault()}
      sx={{
        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
        zIndex: 1300,
        bgcolor: tk.page,
        border: `1px solid ${tk.border}`,
        borderRadius: '12px',
        backdropFilter: 'blur(24px)',
        boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.6)' : '0 18px 48px rgba(14,116,144,0.18)',
        overflow: 'hidden',
      }}
    >
      {searchResults.length > 0 && (
        <Box sx={{
          maxHeight: 360, overflowY: 'auto', p: 1,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '4px' },
        }}>
          {searchResults.map((app, idx) => {
            const color = appColorMap.get(app.appname) || tk.accent;
            const isSelected = idx === spotlightIndex;
            return (
              <Box
                key={app.id}
                ref={(el) => { spotlightItemRefs.current[idx] = el; }}
                onClick={() => {
                  handleAppClick(app);
                  setSearchQuery('');
                  setSpotlightOpen(false);
                  searchRef.current?.blur();
                }}
                onMouseEnter={() => setSpotlightIndex(idx)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer',
                  mb: 0.25,
                  bgcolor: isSelected ? tk.surfaceHover : 'transparent',
                  border: `1px solid ${isSelected ? `${tk.accent}55` : 'transparent'}`,
                  transition: 'background 0.12s, border-color 0.12s',
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: '7px',
                  bgcolor: `${color}1c`, border: `1px solid ${color}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  color,
                }}>
                  {getIcon(app.appname, 14) || (
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color }}>
                      {(app.appname?.[0] || '?').toUpperCase()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '13px', fontWeight: 600, color: tk.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {app.name || app.appname}
                  </Typography>
                  {app.name && (
                    <Typography sx={{
                      fontSize: '11px', color: tk.textSub,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {app.appname}
                    </Typography>
                  )}
                </Box>
                <Box sx={{
                  px: 1, py: 0.15, borderRadius: '5px',
                  bgcolor: `${color}15`, border: `1px solid ${color}30`,
                  flexShrink: 0,
                }}>
                  <Typography sx={{ fontSize: '10px', color, fontWeight: 600 }}>
                    {app.subcategory}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {searchQuery && searchResults.length === 0 && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '12.5px', color: tk.textSub }}>
            No apps found for "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ─── Favorites section ────────────────────────────────────────────────────
  const favoriteApps = useMemo(() => {
    const list = (apps || []).filter((a) => favorites.includes(a.id));
    list.sort((a, b) =>
      (a.name || a.appname || '').toLowerCase().localeCompare((b.name || b.appname || '').toLowerCase())
    );
    return list;
  }, [apps, favorites]);

  const renderFavorites = () => {
    if (favoriteApps.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Box
          onClick={() => setFavSectionOpen((v) => !v)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1, py: 0.5,
            cursor: 'pointer', userSelect: 'none',
            '&:hover .fav-label': { color: tk.star },
          }}
        >
          <Box sx={{
            transition: 'transform 0.2s', color: tk.textSub,
            transform: favSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            display: 'flex', alignItems: 'center',
          }}>
            <ChevronDown size={14} />
          </Box>
          <Star size={13} color={tk.star} fill={tk.star} />
          <Typography
            className="fav-label"
            sx={{
              fontSize: '11.5px', fontWeight: 700,
              letterSpacing: '0.09em', textTransform: 'uppercase',
              color: tk.star, transition: 'color 0.2s',
            }}
          >
            Favorites
          </Typography>
          <Typography sx={{ fontSize: '11px', color: tk.textSub, fontWeight: 600 }}>
            ({favoriteApps.length})
          </Typography>
          <Box sx={{ flex: 1, height: '1px', background: tk.divLine }} />
        </Box>

        {favSectionOpen && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            columnGap: 0, rowGap: 0,
            border: `1px solid ${tk.border}`,
            borderRadius: '10px',
            bgcolor: tk.surface,
            overflow: 'hidden',
          }}>
            {favoriteApps.map((app, idx) => {
              const isLastRow = idx >= favoriteApps.length - (favoriteApps.length % 2 || 2);
              return (
                <Box
                  key={app.id}
                  sx={{
                    borderBottom: isLastRow ? 'none' : `1px solid ${tk.border}`,
                    borderRight: {
                      xs: 'none',
                      sm: idx % 2 === 0 && idx !== favoriteApps.length - 1 ? `1px solid ${tk.border}` : 'none',
                    },
                  }}
                >
                  <AppRow appname={app.appname} appGroup={[app]} dense />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // ─── Recents strip ────────────────────────────────────────────────────────
  const renderRecents = () => {
    if (recentApps.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 0.5 }}>
          <Clock size={12} color={tk.textSub} />
          <Typography sx={{
            fontSize: '11px', fontWeight: 700, color: tk.textSub,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            Recent
          </Typography>
          <Box sx={{ flex: 1, height: '1px', background: tk.divLine }} />
        </Stack>
        <Box sx={{
          display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5,
          '&::-webkit-scrollbar': { height: '4px' },
          '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '4px' },
        }}>
          {recentApps.map((app) => {
            const color = appColorMap.get(app.appname) || tk.accent;
            return (
              <Box
                key={`${app.id}-${app.timestamp}`}
                onClick={() => {
                  const full = apps.find((a) => a.id === app.id) || app;
                  handleAppClick(full);
                }}
                sx={{
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.25, py: 0.75, borderRadius: '8px', cursor: 'pointer',
                  border: `1px solid ${tk.border}`,
                  bgcolor: tk.surface,
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: tk.surfaceHover,
                    borderColor: `${color}55`,
                  },
                  minWidth: 150,
                }}
              >
                <Box sx={{
                  width: 24, height: 24, borderRadius: '6px',
                  bgcolor: `${color}1c`, border: `1px solid ${color}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  color,
                }}>
                  {getIcon(app.appname, 12) || (
                    <Typography sx={{ fontSize: '10px', fontWeight: 800, color }}>
                      {(app.appname?.[0] || '?').toUpperCase()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '12px', fontWeight: 600, color: tk.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {app.name || app.appname}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: tk.textSub, lineHeight: 1.2 }}>
                    {timeAgo(app.timestamp)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // ─── Category sections ────────────────────────────────────────────────────
  const allSubcatGroups = useMemo(() => {
    const out = [];
    Object.entries(groupAppsByCategory).forEach(([, subs]) => {
      Object.entries(subs).forEach(([sub, arr]) => out.push([sub, arr]));
    });
    return out;
  }, [groupAppsByCategory]);

  const renderCategories = () =>
    allSubcatGroups.map(([sub, appsArr]) => {
      const isCollapsed = !!collapsedCategories[sub];
      const groups = Object.entries(groupAppsByAppname(appsArr));
      return (
        <Box key={sub} sx={{ mb: 3 }}>
          <Box
            onClick={() => toggleCategoryCollapse(sub)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 1, py: 0.5,
              cursor: 'pointer', userSelect: 'none',
              '&:hover .cat-label': { color: tk.accent },
            }}
          >
            <Box sx={{
              transition: 'transform 0.2s', color: tk.textSub,
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              display: 'flex', alignItems: 'center',
            }}>
              <ChevronDown size={14} />
            </Box>
            <Typography
              className="cat-label"
              sx={{
                fontSize: '11.5px', fontWeight: 700,
                letterSpacing: '0.09em', textTransform: 'uppercase',
                color: tk.text, transition: 'color 0.2s',
              }}
            >
              {sub}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: tk.textSub, fontWeight: 600 }}>
              ({groups.length})
            </Typography>
            <Box sx={{ flex: 1, height: '1px', background: tk.divLine }} />
          </Box>

          {!isCollapsed && (
            <Box sx={{
              display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              columnGap: 0, rowGap: 0,
              border: `1px solid ${tk.border}`,
              borderRadius: '10px',
              bgcolor: tk.surface,
              overflow: 'hidden',
            }}>
              {groups.map(([appname, group], idx) => {
                const isLastRow = idx >= groups.length - (groups.length % 2 || 2);
                return (
                  <Box
                    key={appname}
                    sx={{
                      borderBottom: isLastRow ? 'none' : `1px solid ${tk.border}`,
                      borderRight: {
                        xs: 'none',
                        sm: idx % 2 === 0 && idx !== groups.length - 1 ? `1px solid ${tk.border}` : 'none',
                      },
                    }}
                  >
                    <AppRow appname={appname} appGroup={group} />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      );
    });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: DRAWER_W },
          maxWidth: '100vw',
          bgcolor: tk.page,
          backgroundImage: 'none',
          color: tk.text,
          borderRight: `1px solid ${tk.border}`,
          backdropFilter: 'blur(24px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.25, pt: 2, pb: 1.5,
        borderBottom: `1px solid ${tk.border}`,
        flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{
            fontSize: '15px', fontWeight: 800,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: tk.text,
          }}>
            Altop
          </Typography>
          <Typography sx={{
            fontSize: '15px', fontWeight: 800,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: tk.text,
          }}>
            Energy
          </Typography>
        </Stack>
        <Tooltip title="Close (Esc)">
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: tk.textSub, borderRadius: '7px',
              '&:hover': { color: tk.accent, bgcolor: tk.accentDim },
            }}
          >
            <X size={16} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Sticky search */}
      <Box sx={{
        px: 2.25, pt: 1.5, pb: 1.5,
        position: 'sticky', top: 0, zIndex: 1100,
        bgcolor: 'transparent',
        borderBottom: `1px solid ${tk.border}`,
        flexShrink: 0,
      }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            onClick={() => searchRef.current?.focus()}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              borderRadius: '10px',
              border: `1px solid ${spotlightOpen ? `${tk.accent}66` : tk.border}`,
              backgroundColor: 'transparent !important',
              backgroundImage: 'none',
              px: 1.5, height: 36, cursor: 'text',
              transition: 'border-color 0.18s',
              '&:hover': {
                borderColor: tk.borderStrong,
              },
            }}
          >
            <Search size={15} color={tk.textSub} />
            <InputBase
              inputRef={searchRef}
              placeholder="Search apps…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSpotlightOpen(true); }}
              onFocus={() => setSpotlightOpen(true)}
              onBlur={() => setTimeout(() => setSpotlightOpen(false), 120)}
              onKeyDown={handleSearchKeyDown}
              fullWidth
              sx={{
                fontSize: '13px', color: tk.text, flex: 1,
                backgroundColor: 'transparent',
                '&.MuiInputBase-root': { backgroundColor: 'transparent' },
                '& input': {
                  backgroundColor: 'transparent !important',
                  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                    WebkitBoxShadow: `0 0 0 1000px ${tk.page} inset`,
                    WebkitTextFillColor: tk.text,
                    caretColor: tk.text,
                    transition: 'background-color 9999s ease-in-out 0s',
                  },
                },
                '& input::placeholder': { color: tk.textSub, opacity: 1 },
              }}
            />
            {searchQuery ? (
              <Typography
                onClick={(e) => { e.stopPropagation(); setSearchQuery(''); searchRef.current?.focus(); }}
                sx={{
                  fontSize: '11px', color: tk.textSub, cursor: 'pointer',
                  px: 0.5, '&:hover': { color: tk.accent },
                }}
              >
                Clear
              </Typography>
            ) : (
              <Typography sx={{
                fontSize: '10.5px', color: tk.textSub, opacity: 0.7,
                whiteSpace: 'nowrap',
              }}>
                ↑↓ · ↵ · esc
              </Typography>
            )}
          </Box>
          {spotlightDropdown}
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        px: 2.25, pt: 2, pb: 3,
        '&::-webkit-scrollbar': { width: '5px' },
        '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '4px' },
      }}>
        {renderRecents()}
        {renderFavorites()}
        {renderCategories()}
      </Box>
    </Drawer>
  );
};

export default MiniDashboardDrawer;
